/**
 * The modal shell around `PdfDocumentView` — the PDF counterpart of
 * `SpreadsheetViewer`, and deliberately the same shape: backdrop, header with
 * the filename, Escape/X to close, and the render delegated to a component that
 * knows nothing about being in a modal.
 *
 * What is extra here is the pager and the escape hatch. A citation must never
 * be a dead click, so whenever the bytes can't be fetched or pdf.js can't parse
 * them the modal degrades to the pre-DOP-404 behaviour: a signed URL opened in
 * a new tab, offered as a button rather than fired automatically (a popup
 * triggered from an async failure is blocked by every browser).
 */

import { ChevronLeft, ChevronRight, ExternalLink, FileText, X } from "lucide-react";
import React, {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { createPortal } from "react-dom";

import { friendlyErrorMessage } from "../utils/apiErrorDetail";
import { openDopplerUri, ParsedDopplerUri } from "../utils/dopplerUri";
import "./PdfViewer.css";

/** A cached PDF resource, mirroring `WorkbookCacheEntry`. `value` is populated
 * once `promise` resolves so a repeat open reads it without a loading flash. */
export interface PdfCacheEntry {
	promise: Promise<ArrayBuffer>;
	value?: ArrayBuffer;
	/** Server-provided filename (from the download response), when resolved. */
	filename?: string;
}

/** Resolve+fetch a PDF's bytes, deduplicated/cached by file identity. Owned by
 * the provider so it survives modal open/close. */
export type LoadPdf = (parsed: ParsedDopplerUri) => PdfCacheEntry;

interface PdfViewerProps {
	parsed: ParsedDopplerUri;
	chatId?: string | null;
	/** Display name in the header; falls back to the server's, then a label. */
	filename?: string;
	loadPdf: LoadPdf;
	onClose: () => void;
}

// react-pdf (and the pdf.js worker it pulls in) is a heavy dependency that
// nothing on first paint needs, so it loads as its own chunk on first open.
const PdfDocumentView = React.lazy(() => import("./PdfDocumentView"));

/**
 * Catches the one failure `Suspense` cannot: the lazy chunk itself failing to
 * arrive (a stale deploy, an offline tab). Without it a missing chunk unmounts
 * the whole chat rather than the viewer, so the escape hatch has to be reached
 * from a render error, not just from a rejected promise.
 */
class PdfChunkBoundary extends React.Component<
	{ onError: (error: Error) => void; children: React.ReactNode },
	{ failed: boolean }
> {
	state = { failed: false };

	static getDerivedStateFromError() {
		return { failed: true };
	}

	componentDidCatch(error: Error) {
		this.props.onError(error);
	}

	render() {
		return this.state.failed ? null : this.props.children;
	}
}

const PdfViewer: React.FC<PdfViewerProps> = ({
	parsed,
	chatId,
	filename,
	loadPdf,
	onClose,
}) => {
	const entry = useMemo(() => loadPdf(parsed), [loadPdf, parsed]);

	const [data, setData] = useState<ArrayBuffer | null>(entry.value ?? null);
	const [resolvedFilename, setResolvedFilename] = useState<string | null>(
		entry.filename ?? null,
	);
	const [error, setError] = useState<string | null>(null);
	const [numPages, setNumPages] = useState<number | null>(null);
	const [page, setPage] = useState<number>(parsed.page ?? 1);

	// Close on Escape.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	useEffect(() => {
		let cancelled = false;
		setError(null);
		entry.promise.then(
			(bytes) => {
				if (cancelled) return;
				setData(bytes);
				setResolvedFilename(entry.filename ?? null);
			},
			(err: unknown) => {
				if (cancelled) return;
				setError(friendlyErrorMessage(err, "Could not open this document."));
			},
		);
		return () => {
			cancelled = true;
		};
	}, [entry]);

	const handleLoadError = useCallback((err: Error) => {
		setError(friendlyErrorMessage(err, "Could not display this document."));
	}, []);

	const openInNewTab = useCallback(() => {
		void openDopplerUri(
			`doppler://pdf/${parsed.target}${page > 1 ? `?page=${page}` : ""}`,
			{ chatId },
		);
	}, [parsed.target, page, chatId]);

	// The highlight belongs to the cited page only; paging away from it must not
	// drag the mark along to a page the citation never claimed.
	const onCitedPage = page === (parsed.page ?? 1);
	const highlight = onCitedPage ? parsed.highlight : undefined;
	const numberHighlight = onCitedPage ? parsed.number : undefined;

	const canPrev = page > 1;
	const canNext = numPages != null && page < numPages;

	return createPortal(
		<div
			className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
			onClick={onClose}
		>
			<div
				className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-800"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-label={filename ?? resolvedFilename ?? "PDF document"}
			>
				<div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
					<div className="flex min-w-0 items-center gap-2">
						<FileText className="h-5 w-5 shrink-0 text-teal-600" />
						<div className="min-w-0">
							<div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
								{filename ?? resolvedFilename ?? "Document"}
							</div>
							<div className="truncate text-xs text-gray-500 dark:text-gray-400">
								{numPages ? `Page ${page} of ${numPages}` : `Page ${page}`}
							</div>
						</div>
					</div>
					<div className="flex items-center gap-1">
						<button
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={!canPrev}
							className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-gray-700"
							aria-label="Previous page"
						>
							<ChevronLeft className="h-4 w-4" />
						</button>
						<button
							onClick={() =>
								setPage((p) => (numPages ? Math.min(numPages, p + 1) : p + 1))
							}
							disabled={!canNext}
							className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-gray-700"
							aria-label="Next page"
						>
							<ChevronRight className="h-4 w-4" />
						</button>
						<button
							onClick={onClose}
							className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
							aria-label="Close"
						>
							<X className="h-4 w-4" />
						</button>
					</div>
				</div>

				{error ? (
					<div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
						<p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
						<button
							onClick={openInNewTab}
							className="inline-flex items-center gap-2 rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
						>
							<ExternalLink className="h-4 w-4" />
							Open in a new tab
						</button>
					</div>
				) : data ? (
					<PdfChunkBoundary onError={handleLoadError}>
						<Suspense
							fallback={
								<div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
									Loading viewer…
								</div>
							}
						>
							<PdfDocumentView
								data={data}
								page={page}
								highlight={highlight}
								numberHighlight={numberHighlight}
								onNumPages={setNumPages}
								onLoadError={handleLoadError}
							/>
						</Suspense>
					</PdfChunkBoundary>
				) : (
					<div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
						Loading document…
					</div>
				)}
			</div>
		</div>,
		document.body,
	);
};

export default PdfViewer;
